/** Aligns with HomePage / Content (blue–pink brand, slate neutrals). Client overview adds muted purple charts. */
const theme = {
  colors: {
    brandPrimary: '#3B82F6',
    brandDark: '#2563eb',
    brandAccent: '#EC4899',
    /** Muted purple — UVC bar chart, client dashboard accents (matches overview mockup) */
    chartPurple: '#5b52c4',
    accentSuccess: '#059669',
    accentWarning: '#d97706',
    accentDanger: '#dc2626',
    accentInfo: '#2563eb',
    bgPrimary: '#FFFFFF',
    bgSecondary: '#f8fafc',
    bgTertiary: '#f1f5f9',
    /** Warm page canvas behind client dashboard card (matches Operations mockup) */
    pageCanvas: '#FAF9F6',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    borderDefault: 'rgba(226, 232, 240, 0.9)',
  },
  radius: {
    md: 12,
    lg: 16,
  },
  gradients: {
    primary: 'linear-gradient(to right, #3B82F6 0%, #EC4899 100%)',
    primaryHover: 'linear-gradient(to right, #2563eb 0%, #db2777 100%)',
    page: 'linear-gradient(180deg, #fafbfc 0%, #f1f5f9 100%)',
    brandMark: 'linear-gradient(135deg, #3B82F6 0%, #EC4899 100%)',
    headerBar: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)',
  },
};

export function conditionColor(type) {
  switch (type) {
    case 'success':
    case 'win':
      return theme.colors.accentSuccess;
    case 'warning':
    case 'attention':
      return theme.colors.accentWarning;
    case 'danger':
    case 'error':
      return theme.colors.accentDanger;
    case 'info':
    case 'informational':
      return theme.colors.accentInfo;
    default:
      return theme.colors.textSecondary;
  }
}

export function conditionBg(type) {
  switch (type) {
    case 'success':
    case 'win':
      return '#ecfdf5';
    case 'warning':
    case 'attention':
      return '#fffbeb';
    case 'danger':
    case 'error':
      return '#fef2f2';
    case 'info':
    case 'informational':
      return '#eff6ff';
    default:
      return theme.colors.bgSecondary;
  }
}

export const tierConfig = {
  Silver: { hours: 24, color: '#94A3B8' },
  Diamond: { hours: 36, color: theme.colors.brandPrimary },
  Platinum: { hours: 60, color: '#D4AF37' },
};

export const areaColors = {
  Patient: '#6d28d9',
  'Patient Rooms': '#6d28d9',
  OP: '#14b8a6',
  OT: '#ea580c',
  ICU: '#38bdf8',
  Other: '#94a3b8',
};

/** Distinct fills for coverage slices whose `name` is not in `areaColors` (API may return any area labels). */
export const coverageChartPalette = [
  '#7c3aed',
  '#0d9488',
  '#ea580c',
  '#1e40af',
  '#64748b',
  '#a855f7',
  '#0891b2',
  '#c2410c',
  '#0369a1',
  '#475569',
  '#be123c',
  '#166534',
  '#7c2d12',
];

/**
 * Donut color for a coverage row: known names use `areaColors`; any other label gets a stable color from
 * `coverageChartPalette` in order of first appearance in `coverageData`.
 */
export function getCoverageAreaColor(name, coverageData) {
  if (name != null && areaColors[name] != null) return areaColors[name];
  const data = coverageData || [];
  const unknownFirstSeen = [];
  const seen = new Set();
  for (const row of data) {
    const n = row?.name;
    if (n == null || areaColors[n] != null) continue;
    if (!seen.has(n)) {
      seen.add(n);
      unknownFirstSeen.push(n);
    }
  }
  const idx = unknownFirstSeen.indexOf(name);
  if (idx === -1) return theme.colors.textSecondary;
  return coverageChartPalette[idx % coverageChartPalette.length];
}

export default theme;
